// src/components/Blog.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  InputBase,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Card,
  CardMedia,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import axios from "axios";

const CATEGORIES = ["All", "News", "Guides", "Analysis", "Announcements"];

export default function Blog() {
  const theme = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        /*  ──────────────────────────────────────────────────────────────
            keep `_embed` (to get featured-media) but DROP `_fields`
            so the image URLs aren’t stripped out.
        ─────────────────────────────────────────────────────────────── */
        const { data } = await axios.get(
          "https://brokepiggytoken.xyz/wp-json/wp/v2/clblog?_embed&per_page=100"
        );
  
        const stripHtml = (html = "") => html.replace(/<[^>]+>/g, "").trim();
  
        const mapped = data.map((post) => {
          /* 1) featured image  */
          const fm   = post?._embedded?.["wp:featuredmedia"]?.[0] || {};
          const img  = fm.source_url || "";          // full-size is always present
  
          /* 2) category label  */
          const category =
            post?._embedded?.["wp:term"]?.[0]?.[0]?.name ?? "Uncategorized";
  
          /* 3) plain-text body */
          const bodyPlain = stripHtml(
            post.contentPlain || post.content?.rendered || ""
          );
  
          /* 4) ≤160-char preview */
          const preview =
            bodyPlain.length > 160 ? bodyPlain.slice(0, 160).trim() + "…" : bodyPlain;
  
          return {
            id:      post.id,
            slug:    post.slug,
            title:   stripHtml(post.title?.rendered),
            image:   img,        // ← now populated
            category,
            date:    post.date,
            content: bodyPlain,
            preview,
          };
        });
  
        setPosts(mapped);
      } catch (err) {
        console.error("Failed to fetch blog posts:", err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchPosts();
  }, []);      // ← run once
  
  
// Track page view on component mount.
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-W97PQD73RV', {
        page_path: window.location.pathname,
      });
    }
  }, []);

  const filtered = posts
    .filter((p) => category === "All" || p.category === category)
    .filter(
      (p) =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const da = new Date(a.date),
        db = new Date(b.date);
      return sortOrder === "newest" ? db - da : da - db;
    });

    <Helmet>
        <title> Blog – Project Tokenization Blog on CrowdLaunch</title>
        <meta
          name="description"
          content="Keep up with the latest articles in project tokenization, Web3, and decentralized finance."
        />
        <meta
          name="keywords"
          content="CrowdLaunch, project tokenization, web3, decentralized finance, startup funding, blockchain, crypto"
        />
        {/* Global Site Tag (gtag.js) - Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-W97PQD73RV"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-W97PQD73RV', { page_path: window.location.pathname });
          `}
        </script>
      </Helmet>

  return (
    
    <Box>
      {/* Hero */}
      <Box
        sx={{
          height: { xs: 200, md: 300 },
          backgroundImage: `url(/banner-blog.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#FFF",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.5)" }} />
        <Container sx={{ position: "relative", textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{ fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" }, mb: 1 }}
          >
            Blog
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.85 }}>
            Insights, guides & news from the world of DeFi
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 3, md: 6 } }}>
        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Search */}
          <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#1C1C1E", borderRadius: 1, px: 1 }}>
            <InputBase
              placeholder="Search articles…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ color: "#FFF", ml: 1, flex: 1 }}
            />
            <IconButton sx={{ color: "#FFD700" }}>
              <SearchIcon />
            </IconButton>
          </Box>
          {/* Sort */}
          <ToggleButtonGroup
  size="small"
  value={sortOrder}
  exclusive
  onChange={(_, val) => val && setSortOrder(val)}
  sx={{
    gap: 1,
    bgcolor: "transparent",          // group container
    "& .MuiToggleButton-root": {
      px: 2,
      py: 0.5,
      borderRadius: "16px",
      fontSize: "0.75rem",
      fontWeight: 600,
      textTransform: "none",
      bgcolor: "#1C1C1E",            // ← dark by default
      border: "1px solid rgba(255,255,255,0.12)",
      color: "#FFF",
      transition: "all .25s ease",
      "&:hover": {
        bgcolor: "rgba(255,255,255,0.08)",
        borderColor: "rgba(255,255,255,0.3)"
      }
    },
    "& .Mui-selected": {
      bgcolor: "#FFD700",
      color: "#1C1E1E",
      borderColor: "#FFD700",
      boxShadow: "0 0 0 2px rgba(255,215,0,0.4)",
      "&:hover": {
        bgcolor: "#FFC700"
      }
    },
    "& .MuiToggleButton-root.Mui-focusVisible": {
      outline: "none"
    }
  }}
>
  <ToggleButton value="newest">Newest</ToggleButton>
  <ToggleButton value="oldest">Oldest</ToggleButton>
</ToggleButtonGroup>

          {/* Categories */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                clickable
                onClick={() => setCategory(cat)}
                sx={{
                  bgcolor: category === cat ? "#FFD700" : "#1C1C1E",
                  color: category === cat ? "#1C1E1E" : "#FFF",
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Loading / Empty */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#FFD700" }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ color: "#BBB", textAlign: "center" }}>
            No articles match your criteria.
          </Typography>
        ) : (
          /* Grid of Cards */
          <Grid container spacing={3}>
 {filtered.map((post) => {
    /* 100-character preview from the full plain-text body */
    const body     = post.content ?? "";
    const preview  = body.length > 100 ? body.slice(0, 100).trim() + "…" : body;

    return (
      <Grid item xs={12} sm={6} md={4} key={post.id}>
  <Card
    sx={{
      position: "relative",
      overflow: "hidden",
      borderRadius: 2,
      background: "linear-gradient(180deg,#171717 0%,#111112 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow: "0 8px 22px rgba(0,0,0,0.55)",
      transition: "transform .35s, box-shadow .35s, border-color .35s",
      "&:hover": {
        transform: "translateY(-8px)",
        boxShadow: "0 14px 34px rgba(0,0,0,0.7)",
        borderColor: "#FFD70088",
        "& .hoverBar": { width: "100%" }
      }
    }}
  >
    {/* gold bar animates on hover */}
    <Box
      className="hoverBar"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        height: 3,
        width: 0,
        bgcolor: "#FFD700",
        transition: "width .35s"
      }}
    />

    <CardActionArea component={Link} to={`/blog/${post.slug}`} sx={{ height: "100%" }}>
      {/* Banner + overlay */}
      {post.image && (
        <Box sx={{ position: "relative" }}>
          <CardMedia
            component="img"
            height="200"
            image={post.image}
            alt={post.title}
            sx={{ objectFit: "cover" }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(0deg, rgba(0,0,0,0.65) 5%, rgba(0,0,0,0.0) 60%)"
            }}
          />
        </Box>
      )}

      <CardContent sx={{ pb: 2 }}>
        {/* Title */}
        <Typography
          gutterBottom
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            fontSize: "1.05rem",
            lineHeight: 1.25,
            color: "#FFF"
          }}
        >
          {post.title}
        </Typography>

        {/* 100-char preview */}
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.85)",
            mb: 1.75,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden"
          }}
        >
          {preview}
        </Typography>

        {/* footer row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Chip
            label={post.category}
            size="small"
            sx={{
              bgcolor: "#FFD700",
              color: "#1C1E1E",
              fontSize: "0.65rem",
              fontWeight: 600
            }}
          />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            {new Date(post.date).toLocaleDateString()}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
</Grid>


    );
  })}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
